import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { push } from 'connected-react-router';

import Strings from '../../Strings';
import { EndedEnrollmentShippingWarning } from '../../components/EnrollmentShippingWarning';
import { shippingActions } from './actions';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import { openModalAction } from '../../actions/modal';
import { DATE_MONTH_DAY_YEAR } from '../../constants';
import { ActionButton, ActionWidget } from '../../components/ActionWidget';
import { shipmentStatusEnum as shipmentStatus } from '../../modals/_ModalsMetadata/Fields/shipmentFields';
import { getDeliveredDate, getShippedDate } from './ShipmentsTabs';
import { hasPermission, PERMISSIONS } from '../../utils/userPermissions';
import { isShipper, isSuperUser } from '../../utils/userRoles';
import { ReturnMethodEnum } from './ShippingOrders';
import { toolTipForTable } from '../../utils';

export const renderShipmentAddress = address => {
  if (!address) {
    return '-';
  }
  const { street, street2, city, state, zip, country } = address;
  return toolTipForTable([street, street2, city, state, zip, country].filter(s => s).join(', '), [street, street2, city, state, zip, country].filter(s => s));
};

const renderShipmentIdCell = info => (
  <div className="icon-and-text-container">
    <div className="icon-and-text-content">{info.getValue() || '-'}</div>
    <EndedEnrollmentShippingWarning shipment={info.row.original} compact />
  </div>
);

function ShipmentList(props) {
  const {
    shipments,
    loading,
    status,
    editShipment,
    openConfirmModal,
    refreshData,
    orgs,
    showShipmentDetails,
    onNavigate,
    pagination,
    onOffsetChange,
    onPageSizeChange,
    onSortingChange,
  } = props;
  const [selectedShipments, setSelectedShipments] = React.useState([]);
  const tableRef = React.useRef(null);
  const canEdit = hasPermission(PERMISSIONS.SHIPPING_SHIPMENTS_EDIT) || isShipper() || isSuperUser();

  const markShipmentDelivered = shipment => {
    editShipment({
      shipment,
      onSuccess: refreshData,
      mode: 'delivered',
    });
  };

  const orderIdCell = info => {
    const order_uuid = info.row.original.order_uuid;
    return (
      <div
        onClick={e => {e.preventDefault(); e.stopPropagation(); onNavigate(`/shipping/order-details/${order_uuid}`);}}
        className="clickable"
      >
        {info.renderValue()}
      </div>
    );
  };

  const columns = React.useMemo(() => {
    let ret = [
      columnHelper.accessor('shipment_uuid', {
        header: Strings.shipping.shipmentId,
        cell: renderShipmentIdCell,
        size: 250,
      }),
      columnHelper.accessor('order_uuid', {
        header: Strings.shipping.orderId,
        cell: info => orderIdCell(info),
        size: 250,
      }),
      columnHelper.accessor('flow', {
        header: Strings.shipping.shippingFlow,
        cell: info => info.getValue() ? (Strings.shipping.shippingFlows[info.getValue()] || info.getValue()) : '-',
        size: 200,
      }),
      columnHelper.accessor('order_category', {
        header: Strings.shipping.orderCategory,
        cell: info => Strings.shipping.orderCategories[info.getValue()] || info.getValue(),
        size: 150,
      }),
      columnHelper.accessor('recipient_name', {
        header: Strings.shipping.recipientName,
        cell: info => info.getValue() || '-',
        accessorFn: row => row?.contact_details?.recipient_name || '-',
        size: 200,
      }),
      columnHelper.accessor('pharmacy_patient_id', {
        header: Strings.capPatient.patientId,
        cell: info => info.getValue() || '-',
        size: 150,
      }),
      columnHelper.accessor('fulfillment_provider', {
        header: Strings.shipping.fulfillmentProvider,
        cell: info => info.getValue() || '-',
        size: 200,
      }),
    ];
    if (status !== shipmentStatus.new && status !== shipmentStatus.cancelled) {
      ret.push(
        columnHelper.accessor('tracking_number', {
          header: Strings.shipping.trackingNumber,
          cell: info => info.getValue() || '-',
          size: 250,
        }),
      );
      ret.push(
        columnHelper.accessor('shipping_method', {
          header: Strings.shipping.shippingMethod,
          cell: info => info.getValue() || '-',
          size: 250,
        }),
      );
      ret.push(
        columnHelper.accessor('provider_order_no', {
          header: Strings.shipping.providerOrderNumber,
          cell: info => info.getValue() || '-',
          size: 150,
        }),
      );
      ret.push(
        columnHelper.accessor('sales_order_no', {
          header: Strings.shipping.salesOrderNumber,
          cell: info => info.getValue() || '-',
          size: 150,
        }),
      );
    }
    ret = ret.concat([
      columnHelper.accessor('orgName', {
        header: Strings.organization,
        cell: info => info.getValue() || '-',
        size: 200,
      }),
    ]);
    if (status === shipmentStatus.new) {
      ret.push(
        columnHelper.accessor('sales_order_no', {
          header: Strings.shipping.salesOrderNumber,
          cell: info => info.getValue() || '-',
        }),
      );
      ret.push(
        columnHelper.accessor('provider_order_no', {
          header: Strings.shipping.providerOrderNumber,
          cell: info => info.getValue() || '-',
          size: 230,
        }),
      );
    }
    ret = ret.concat([
      columnHelper.accessor('shipping_address', {
        header: Strings.shipping.shippingAddress,
        cell: info => info.getValue() || '-',
        accessorFn: row => {
          if (status === shipmentStatus.new || status === shipmentStatus.cancelled) {
            return renderShipmentAddress(row?.patient_address) || '-';
          }
          return renderShipmentAddress(row?.shipping_address) || '-';
        },
        size: 350,
      }),
      columnHelper.accessor('phone', {
        header: Strings.phoneNo,
        cell: info => info.getValue() || '-',
        accessorFn: row => row?.contact_details?.phone,
        size: 160,
      }),
      columnHelper.accessor('email', {
        header: Strings.email,
        cell: info => info.getValue() || '-',
        accessorFn: row => row?.contact_details?.email,
        size: 200,
      }),
    ]);
    if (status === shipmentStatus.new) {
      ret.push(
        columnHelper.accessor('created_at', {
          header: Strings.createdAt,
          cell: info => info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-',
          sortingFn: 'dateSortingFn',
          sortUndefined: -1,
        }),
      );
    } else {
      ret.push(
        columnHelper.accessor('shipped_date', {
          header: Strings.shipping.shippingDate,
          accessorFn: info => {
            const shippedDate = getShippedDate(info);
            return shippedDate ? moment(shippedDate).format(DATE_MONTH_DAY_YEAR) : '-';
          },
          size: 160,
          sortingFn: 'dateSortingFn',
          sortUndefined: -1,
        }),
      );
    }
    if (status === shipmentStatus.in_transit) {
      ret.push(
        columnHelper.accessor('eta', {
          header: Strings.shipping.eta,
          cell: info => info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-',
          size: 160,
          sortingFn: 'dateSortingFn',
          sortUndefined: -1,
        }),
      );
    }
    if (status === shipmentStatus.delivered) {
      ret.push(
        columnHelper.accessor('delivered_at', {
          header: Strings.shipping.deliveryDate,
          accessorFn: row => {
            const deliveredDate = getDeliveredDate(row);
            return deliveredDate ? moment(deliveredDate).format(DATE_MONTH_DAY_YEAR) : '-';
          },
          size: 160,
          sortingFn: 'dateSortingFn',
          sortUndefined: -1,
        }),
      );
    }
    ret.push(
      columnHelper.accessor('kit_ids', {
        cell: info => info.getValue()?.join(', ') || '-',
        header: Strings.kitIds,
        size: 250,
      }),
    );
    return ret;
  }, [status]);

  // While a live SmartMeter order exists, the vendor's webhooks own the
  // shipment's transitions and dates; the backend rejects manual ones.
  const isLiveSmartMeter = shipment =>
    shipment?.fulfillment_provider === 'smartmeter' &&
    !['cancelled', 'error'].includes(shipment?.smartmeter_status);

  const buttons = React.useMemo(() => {
    const ret = [];
    ret.push({
      icon: 'check',
      onClick: (_, shipment) => markShipmentDelivered(shipment),
      text: Strings.shipping.markAsDelivered,
      visible: (_, shipment) =>
        !isLiveSmartMeter(shipment)
      && (shipment?.status === shipmentStatus.in_transit
      || (shipment?.status === shipmentStatus.created && shipment?.return_method === ReturnMethodEnum.pharmacy_walk_in)
      ),
    });
    if (status === shipmentStatus.new) {
      ret.push({
        icon: 'shipping',
        onClick: (_, shipment) =>
          editShipment({
            shipment,
            onSuccess: refreshData,
            mode: 'ship',
          }),
        text: Strings.shipping.sendPackage,
        visible: (_, shipment) => !isLiveSmartMeter(shipment),
        disabled: (_, shipment) => shipment?.enrollment_details?.some(e => e.status === 'stopped'),
        disabledTooltip: Strings.capPatient.enrollmentIsCancelled,
      });
    }
    if (status === shipmentStatus.in_transit || status === shipmentStatus.new) {
      ret.push({
        icon: 'edit',
        onClick: (_, shipment) =>
          editShipment({
            shipment,
            onSuccess: refreshData,
            mode: 'edit',
          }),
        text: Strings.edit,
      });
    }
    ret.push({
      icon: 'print',
      onClick: (_, shipment) => showShipmentDetails({ shipments: [shipment], orgs }),
      text: Strings.shipping.printShipmentDetails,
    });
    if (status === shipmentStatus.new) {
      ret.push({
        icon: 'delete',
        onClick: (_, shipment) =>
          openConfirmModal({
            title: Strings.formatString(
              Strings.shipping.cancelShipmentQuestion,
              shipment.contact_details.recipient_name,
            ),
            onConfirmAction: shippingActions.editShipment(shipment.shipment_uuid, { status: shipmentStatus.cancelled }),
            caption: Strings.shipping.cancelShipment,
            hideCaution: true,
            confirmPostAction: refreshData,
          }),
        text: Strings.shipping.cancelShipment,
        // A SmartMeter-managed placeholder must be cancelled at the vendor
        // first; the backend rejects a direct shipment cancel for it.
        visible: (_, shipment) => shipment?.fulfillment_provider !== 'smartmeter',
      });
      ret.push({
        icon: 'delete',
        onClick: (_, shipment) =>
          openConfirmModal({
            title: Strings.formatString(
              Strings.shipping.cancelSmartmeterOrderQuestion,
              shipment.contact_details?.recipient_name || shipment.provider_order_no || '',
            ),
            onConfirmAction: shippingActions.cancelSmartmeterOrder(shipment.order_uuid),
            caption: Strings.shipping.cancelSmartmeterOrder,
            hideCaution: true,
            confirmPostAction: refreshData,
          }),
        text: Strings.shipping.cancelSmartmeterOrder,
        visible: (_, shipment) =>
          shipment?.fulfillment_provider === 'smartmeter' && (isShipper() || isSuperUser()),
        // The vendor accepts a cancel only while the fulfillment is submitted
        // (or already cancelling); other states would just 409.
        disabled: (_, shipment) => !['submitted', 'cancelling'].includes(shipment?.smartmeter_status),
        disabledTooltip: Strings.shipping.cancelSmartmeterOrderNotCancellable,
      });
    }
    return ret;
  }, [status, shipments, canEdit]);

  const actionBtns = (
    <ActionButton
      img="action-print"
      tooltiptext={Strings.shipping.printShipmentDetails}
      action={() => {
        showShipmentDetails({ shipments: selectedShipments, orgs, onSuccess: () => tableRef?.current?.resetSelectedRows() });
      }}
    />
  );

  const onRowClick = row => {
    showShipmentDetails({ shipments: [row], orgs });
  };

  return (
    <div className="tab-page-content">
      <Table3
        data={shipments}
        isLoading={loading}
        columns={columns}
        name="Shipments"
        buttons={canEdit ? buttons : undefined}
        enableColumnFiltering
        enableRowSelection
        pinFirstColumn
        onRowClick={onRowClick}
        onRowSelectionChange={setSelectedShipments}
        ref={tableRef}
        pagination={pagination}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
      />
      <ActionWidget show={selectedShipments?.length > 0}>{actionBtns}</ActionWidget>
    </div>
  );
}

ShipmentList.propTypes = {
  shipments: PropTypes.array.isRequired,
  editShipment: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  refreshData: PropTypes.func.isRequired,
  orgs: PropTypes.array.isRequired,
  showShipmentDetails: PropTypes.func.isRequired,
  pagination: PropTypes.object.isRequired,
  onOffsetChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onSortingChange: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  editShipment: data => dispatch(openModalAction('edit-shipment', data)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  showShipmentDetails: data => dispatch(openModalAction('shipment-details', data)),
  onNavigate: url => dispatch(push(url)),
});

export default connect(null, mapDispatchToProps)(ShipmentList);
